/**
 * Mental Map room — what the join link opens.
 *
 * Public by design: whoever holds the link joins with a name only. The person
 * who opened the room uses the same link; being signed in is what makes them
 * the host.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';

import { TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { rememberedName, useLiveRoom } from '@/features/mental-map/liveRoom';
import { MentalMapRoom } from '@/features/mental-map/MentalMapRoom';
import { isAuthenticated } from '@/lib/supabaseSync';

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen items-center justify-center bg-[var(--bg)] px-6 text-[var(--text-pri)]">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

function NameForm({ prompt, join }: { prompt: string; join: (name: string) => Promise<string | null> }) {
  const [name, setName] = useState(rememberedName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(await join(name));
    setBusy(false);
  };

  return (
    <Centered>
      <p className="font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]">Mental map</p>
      <h1 className="mt-1 font-heading text-2xl font-semibold">{prompt}</h1>
      <p className="mt-3 font-body text-sm text-[var(--text-sec)]">
        You will draw how you see it, in your own space. Nobody else sees your map until you show it, or until
        everyone&rsquo;s maps are revealed together.
      </p>
      <form className="mt-6 space-y-3" onSubmit={(e) => void submit(e)}>
        <label className="block font-body text-sm text-[var(--text-sec)]" htmlFor="mental-map-name">
          Your name
        </label>
        <TextInput
          id="mental-map-name"
          value={name}
          maxLength={60}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="As the others should see it"
        />
        {error && <p className="font-body text-sm text-[var(--coral)]">{error}</p>}
        <Button type="submit" variant="cta" disabled={!name.trim() || busy}>
          {busy ? 'Joining…' : 'Join and start drawing'}
        </Button>
      </form>
    </Centered>
  );
}

export function MentalMapJoin() {
  const { token = '' } = useParams<{ token: string }>();
  const state = useLiveRoom(token);
  // Guests have no Spaces; a signed-in person can keep what they draw in theirs.
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    void isAuthenticated().then(setSignedIn);
  }, []);

  if (state.status === 'loading') {
    return (
      <Centered>
        <p className="font-body text-sm text-[var(--text-sec)]">Opening the room…</p>
      </Centered>
    );
  }
  if (state.status === 'error') {
    return (
      <Centered>
        <h1 className="font-heading text-xl font-semibold">This room could not be opened</h1>
        <p className="mt-2 font-body text-sm text-[var(--text-sec)]">{state.message}</p>
      </Centered>
    );
  }
  if (state.status === 'needs_name') {
    return <NameForm prompt={state.prompt} join={state.join} />;
  }
  return (
    <div className="flex h-screen flex-col">
      {state.saveError && (
        <p role="alert" className="border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-1.5 font-body text-xs text-[var(--coral)]">
          {state.saveError}
        </p>
      )}
      <div className="min-h-0 flex-1">
        <MentalMapRoom room={{ ...state.room, canSaveToSpace: signedIn }} />
      </div>
    </div>
  );
}
